/* eslint-disable */
(() => {
  if (window.__stickyProductCTASyncInitialized) return;
  window.__stickyProductCTASyncInitialized = true;

  const stickyVisibilityInstances = new WeakMap();

  /**
   * Sets up price syncing for one sticky CTA bar.
   * When a swatch changes inside the bar's product-card-with-variants,
   * we look up the matching variant from the embedded JSON and update
   * the price elements with the pre-formatted strings from Liquid.
   */
  const initStickyVariantPriceSync = (stickyCTA) => {
    const card = stickyCTA.querySelector('product-card-with-variants');
    const priceEl = stickyCTA.querySelector('[data-sticky-sale-price]');
    const compareEl = stickyCTA.querySelector('[data-sticky-compare-price]');
    if (!card || !priceEl || !compareEl) return;

    let variants;
    try {
      variants = JSON.parse(card.querySelector('script.variants').textContent).variants;
    } catch (e) {
      return;
    }
    if (!variants?.length) return;

    // Build { OptionName: selectedValue } from checked swatches inside the card
    const getSelectedOptions = () => {
      const selected = {};
      card.querySelectorAll('product-swatch input[type="radio"]:checked').forEach((input) => {
        const optName = input.closest('.swatch-element')?.getAttribute('data-option');
        if (optName) selected[optName] = input.value;
      });
      return selected;
    };

    // Find the variant whose options object matches every selected option
    const findVariant = (selected) =>
      variants.find((v) =>
        Object.entries(selected).every(([key, val]) => v.options[key] === val)
      ) ?? null;

    const updatePrice = () => {
      const variant = findVariant(getSelectedOptions());
      if (!variant) return;

      const onSale = Boolean(variant.compare_at_price);

      priceEl.textContent = variant.price;
      priceEl.classList.toggle('price--sale-price', onSale);
      priceEl.classList.toggle('price--regular-price', !onSale);

      compareEl.textContent = onSale ? variant.compare_at_price : '';
      compareEl.classList.toggle('hidden', !onSale);
    };

    card.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) {
        requestAnimationFrame(updatePrice);
      }
    });
  };

  const setStickyDisplay = (stickyCTA, shouldShow) => {
    const nextDisplay = shouldShow ? 'block' : 'none';
    if (stickyCTA.style.display !== nextDisplay) {
      stickyCTA.style.display = nextDisplay;
    }
  };

  const getStickyHeader = () => {
    if (window.matchMedia('(max-width: 989px)').matches) {
      return document.querySelector('nav.mobile-nav-bar-wrapper');
    }

    return document.querySelector('header.site-header');
  };

  const syncStickyTop = (stickyCTA) => {
    const header = getStickyHeader();
    const headerHeight = header ? Math.max(header.offsetHeight - 2, 0) : 0;
    stickyCTA.style.top = `${headerHeight}px`;
  };

  const getTopSentinel = () => {
    let sentinel = document.querySelector('[data-sticky-cta-top-sentinel]');
    if (sentinel) return sentinel;

    sentinel = document.createElement('span');
    sentinel.setAttribute('data-sticky-cta-top-sentinel', '');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:200px;left:0;width:1px;height:1px;pointer-events:none;opacity:0;';
    document.body.appendChild(sentinel);
    return sentinel;
  };

  const getLastElement = (selector) => {
    const elements = document.querySelectorAll(selector);
    return elements.length ? elements[elements.length - 1] : null;
  };

  const initStickyVisibility = ({ stickySelector, triggerSelector }) => {
    if (typeof IntersectionObserver === 'undefined') return;

    const stickyCTA = document.querySelector(stickySelector);
    const trigger = getLastElement(triggerSelector);
    if (!stickyCTA || !trigger) return;

    const existingInstance = stickyVisibilityInstances.get(stickyCTA);
    if (existingInstance) existingInstance.destroy();

    let triggerVisible = true;
    let nearTop = (window.scrollY || window.pageYOffset || 0) < 200;
    let nearFooter = false;
    let updateFrame = null;
    let resizeFrame = null;
    let headerObserver = null;

    const updateVisibility = () => {
      updateFrame = null;
      setStickyDisplay(stickyCTA, !(nearTop || triggerVisible || nearFooter));
    };

    const scheduleVisibilityUpdate = () => {
      if (updateFrame) return;
      updateFrame = window.requestAnimationFrame(updateVisibility);
    };

    const syncTopOnNextFrame = () => {
      if (resizeFrame) return;
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;
        syncStickyTop(stickyCTA);
        scheduleVisibilityUpdate();
      });
    };

    const observeHeader = () => {
      if (headerObserver) headerObserver.disconnect();
      if (typeof ResizeObserver === 'undefined') return;

      const header = getStickyHeader();
      if (!header) return;

      headerObserver = new ResizeObserver(syncTopOnNextFrame);
      headerObserver.observe(header);
    };

    const visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === trigger) {
          triggerVisible = entry.isIntersecting;
        } else if (entry.target.hasAttribute('data-sticky-cta-top-sentinel')) {
          nearTop = entry.isIntersecting || entry.boundingClientRect.top > 0;
        } else {
          nearFooter = entry.isIntersecting;
        }
      });

      scheduleVisibilityUpdate();
    }, { threshold: 0 });

    visibilityObserver.observe(trigger);
    visibilityObserver.observe(getTopSentinel());

    const footerImage = document.querySelector('.footer-image');
    const footerTarget = footerImage?.children.length ? footerImage : document.querySelector('footer');
    if (footerTarget) {
      visibilityObserver.observe(footerTarget);
    }

    const onResize = () => {
      syncTopOnNextFrame();
      observeHeader();
    };

    syncStickyTop(stickyCTA);
    observeHeader();
    scheduleVisibilityUpdate();
    window.addEventListener('resize', onResize, { passive: true });

    stickyVisibilityInstances.set(stickyCTA, {
      destroy() {
        visibilityObserver.disconnect();
        if (headerObserver) headerObserver.disconnect();
        window.removeEventListener('resize', onResize);
        if (updateFrame) window.cancelAnimationFrame(updateFrame);
        if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
      }
    });
  };

  const initAll = () => {
    document.querySelectorAll('.sticky-product-cta').forEach(initStickyVariantPriceSync);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  window.initStickyProductCTAVisibility = initStickyVisibility;
  window.dispatchEvent(new CustomEvent('stickyProductCTA:ready'));
})();
